const API_BASE_URL = import.meta.env.VITE_API_URL || '';

const ERROR_QUEUE_KEY = 'clientErrorQueue';
const ERROR_QUEUE_MAX = 25;
const ERROR_FLUSH_COOLDOWN_MS = 30000;
const ERROR_REPORT_MAX_BYTES = 32000;
const ERROR_DATA_TRUNCATE_CHARS = 8192;
const CLIENT_REQUEST_ID_HEADER = 'X-Client-Request-Id';

const textEncoder = new TextEncoder();

interface NetworkInformation {
    effectiveType?: string;
    type?: string;
    downlink?: number;
    rtt?: number;
    saveData?: boolean;
}

function createClientRequestId(): string {
    try {
        return crypto.randomUUID();
    } catch {
        // randomUUID can be unavailable in older browsers or non-secure contexts.
        return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
    }
}

function getTransportContext(
    clientRequestId: string,
    requestStartedAt: number,
    requestAttempt: number,
): Record<string, unknown> {
    const connection = (navigator as Navigator & { connection?: NetworkInformation }).connection;

    return {
        clientRequestId,
        elapsedMs: Math.max(0, Math.round(performance.now() - requestStartedAt)),
        requestAttempt,
        online: navigator.onLine,
        visibilityState: document.visibilityState,
        pageAgeMs: Math.max(0, Math.round(performance.now())),
        pageWasDiscarded: Boolean((document as Document & { wasDiscarded?: boolean }).wasDiscarded),
        serviceWorkerControlled: Boolean(navigator.serviceWorker?.controller),
        connectionType: connection?.type,
        connectionEffectiveType: connection?.effectiveType,
        connectionDownlinkMbps: connection?.downlink,
        connectionRttMs: connection?.rtt,
        connectionSaveData: connection?.saveData,
    };
}

/** Payload accepted by POST /api/client-errors. Extra metadata belongs in `context`. */
export interface ClientErrorReport {
    endpoint: string;
    method?: string;
    status: number;
    errorData: unknown;
    context?: Record<string, unknown>;
}

function isClientErrorReport(entry: unknown): entry is ClientErrorReport {
    return typeof entry === 'object' && entry !== null;
}

function readErrorQueue(): ClientErrorReport[] {
    try {
        const raw = localStorage.getItem(ERROR_QUEUE_KEY);
        const parsed: unknown = raw ? JSON.parse(raw) : null;
        // Corrupt entries are dropped here so they cannot block the reports behind them.
        return Array.isArray(parsed) ? parsed.filter(isClientErrorReport) : [];
    } catch {
        // localStorage unavailable or the stored payload is corrupt.
        return [];
    }
}

function writeErrorQueue(reports: ClientErrorReport[]): void {
    try {
        // Bounded queue - the oldest reports are dropped first.
        localStorage.setItem(ERROR_QUEUE_KEY, JSON.stringify(reports.slice(-ERROR_QUEUE_MAX)));
    } catch {
        // localStorage unavailable or full.
    }
}

function queueErrorReport(report: ClientErrorReport): void {
    const queue = readErrorQueue();
    queue.push(report);
    writeErrorQueue(queue);
}

/** Size of the request body as sent - the keepalive limit is in bytes, not characters. */
function serializedByteLength(report: ClientErrorReport): number {
    return textEncoder.encode(JSON.stringify(report)).length;
}

/**
 * Stamps the capture time so delayed deliveries stay correlatable with server logs, and
 * truncates oversized payloads - browsers reject keepalive bodies above ~64KiB, so an
 * outsized report would never deliver and would block the whole queue behind it.
 */
function prepareErrorReport(report: ClientErrorReport): ClientErrorReport {
    const context: Record<string, unknown> = { ...(report.context ?? {}) };
    if (typeof context.occurredAt !== 'string') {
        context.occurredAt = new Date().toISOString();
    }

    let prepared: ClientErrorReport = { ...report, context };
    if (serializedByteLength(prepared) <= ERROR_REPORT_MAX_BYTES) {
        return prepared;
    }

    // errorData is the usual cause and the least useful in bulk, so shrink it first.
    const serialized: string | undefined = JSON.stringify(report.errorData);
    if (report.errorData !== null && serialized !== undefined && serialized.length > ERROR_DATA_TRUNCATE_CHARS) {
        context.errorDataTruncated = true;
        prepared = { ...prepared, errorData: serialized.slice(0, ERROR_DATA_TRUNCATE_CHARS), context };
        if (serializedByteLength(prepared) <= ERROR_REPORT_MAX_BYTES) {
            return prepared;
        }
    }

    // Still oversized, so the bulk sits in context - keep only the correlation keys.
    const reduced: Record<string, unknown> = {
        contextTruncated: true,
        occurredAt: context.occurredAt,
        clientRequestId: context.clientRequestId,
        elapsedMs: context.elapsedMs,
        requestAttempt: context.requestAttempt,
    };
    if (context.errorDataTruncated === true) {
        reduced.errorDataTruncated = true;
    }
    if (context.deliveredFromQueue === true) {
        reduced.deliveredFromQueue = true;
    }

    return { ...prepared, context: reduced };
}

/** Resolves to true only when the server accepted the report. Never throws and never rejects. */
function deliverErrorReport(report: ClientErrorReport): Promise<boolean> {
    try {
        return fetch(`${API_BASE_URL}/api/client-errors`, {
            method: 'POST',
            credentials: 'include',
            keepalive: true,
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(report),
        })
            .then(response => response.ok)
            .catch(() => false);
    } catch {
        // A restricted or monkeypatched fetch can throw synchronously.
        return Promise.resolve(false);
    }
}

/**
 * Reports client errors to the server for CloudWatch logging.
 * Never throws and never rejects - undeliverable reports are queued in localStorage.
 */
export function reportErrorToServer(errorInfo: ClientErrorReport): void {
    let report = errorInfo;

    try {
        report = prepareErrorReport(errorInfo);
        void deliverErrorReport(report)
            .then(delivered => {
                if (!delivered) {
                    queueErrorReport(report);
                }
            })
            .catch(() => {
                queueErrorReport(report);
            });
    } catch {
        // Building or dispatching the report threw synchronously.
        queueErrorReport(report);
    }
}

let errorFlushInFlight = false;
let errorFlushedAt = 0;

/**
 * Delivers queued reports one by one, stopping at the first failure so ordering
 * and at-least-once delivery are preserved. Delivers at most ERROR_QUEUE_MAX reports
 * per invocation. Never throws and never rejects.
 */
export async function flushErrorQueue(): Promise<void> {
    if (errorFlushInFlight || Date.now() - errorFlushedAt < ERROR_FLUSH_COOLDOWN_MS) {
        return;
    }
    if (readErrorQueue().length === 0) {
        return;
    }

    errorFlushInFlight = true;
    errorFlushedAt = Date.now();

    try {
        // Bounded - a silently failing localStorage write must not spin the loop forever.
        for (let sent = 0; sent < ERROR_QUEUE_MAX; sent += 1) {
            const pending = readErrorQueue();
            const next = pending[0];
            if (!next) {
                break;
            }

            const delivered = await deliverErrorReport({
                ...next,
                context: { ...(next.context ?? {}), deliveredFromQueue: true },
            });
            if (!delivered) {
                break;
            }

            // Re-read so reports queued during delivery are not discarded.
            writeErrorQueue(readErrorQueue().slice(1));
        }
    } finally {
        errorFlushInFlight = false;
    }
}

window.addEventListener('online', () => {
    void flushErrorQueue();
});

void flushErrorQueue();

export class ApiError extends Error {
    public readonly status: number;
    public readonly data: unknown;
    public readonly isTransient: boolean;

    constructor(status: number, message: string, data?: unknown) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.data = data;
        this.isTransient = [502, 503, 504].includes(status);
    }
}

/** Body of a 402 response, as sent by the API's InsufficientCreditsExceptionListener. */
interface InsufficientCreditsErrorBody {
    error: string;
    required: number;
    available: number;
    message?: string;
}

/** Detail of the `credits:insufficient` window event. */
export interface InsufficientCreditsDetail {
    creditsNeeded: number;
    creditsAvailable: number;
}

function isInsufficientCreditsErrorBody(data: unknown): data is InsufficientCreditsErrorBody {
    return (
        typeof data === 'object' &&
        data !== null &&
        'required' in data &&
        'available' in data &&
        typeof data.required === 'number' &&
        typeof data.available === 'number'
    );
}

/** Turns a 402 body into the credits the user is missing and the credits the user has. */
export function toInsufficientCreditsDetail(data: unknown): InsufficientCreditsDetail | null {
    if (!isInsufficientCreditsErrorBody(data)) {
        return null;
    }

    // Credits can be fractional, so round away floating-point noise from the subtraction.
    const missing = Math.round((data.required - data.available) * 100) / 100;

    return {
        creditsNeeded: Math.max(0, missing),
        creditsAvailable: data.available,
    };
}

/**
 * A 401 from these endpoints does not mean an expired session: login reports bad
 * credentials, and logout reports that the session is already gone.
 */
const SESSION_EXPIRY_EXEMPT_ENDPOINTS = ['/api/login', '/api/logout'];

function isSessionExpiryExempt(endpoint: string): boolean {
    const path = endpoint.split('?')[0] ?? endpoint;

    return SESSION_EXPIRY_EXEMPT_ENDPOINTS.includes(path);
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
    body?: unknown;
    /** Skip the automatic 402 insufficient credits dialog */
    skipInsufficientCreditsDialog?: boolean;
    /** Internal counter — do not set manually */
    _retryCount?: number;
    /** Internal correlation ID — do not set manually */
    _clientRequestId?: string;
    /** Internal monotonic start time — do not set manually */
    _requestStartedAt?: number;
}

export async function apiFetch<T>(
    endpoint: string,
    options: RequestOptions = {},
): Promise<T> {
    const {
        body,
        headers,
        skipInsufficientCreditsDialog,
        _retryCount = 0,
        _clientRequestId,
        _requestStartedAt,
        ...restOptions
    } = options;

    const method = restOptions.method || 'GET';
    const clientRequestId = _clientRequestId ?? createClientRequestId();
    const requestStartedAt = _requestStartedAt ?? performance.now();
    const requestHeaders = new Headers(headers);
    if (!requestHeaders.has('Content-Type')) {
        requestHeaders.set('Content-Type', 'application/json');
    }
    if (!requestHeaders.has('Accept')) {
        requestHeaders.set('Accept', 'application/json');
    }
    requestHeaders.set(CLIENT_REQUEST_ID_HEADER, clientRequestId);

    let response: Response;
    try {
        response = await fetch(`${API_BASE_URL}${endpoint}`, {
            ...restOptions,
            credentials: 'include',
            headers: requestHeaders,
            body: body ? JSON.stringify(body) : undefined,
        });
    } catch (e) {
        // Network-level failure - no response exists, so nothing below can observe it.
        const error = e instanceof Error ? e : new Error(String(e));
        const errorInfo = {
            endpoint,
            method,
            status: 0,
            errorData: null,
            context: {
                ...getTransportContext(clientRequestId, requestStartedAt, _retryCount + 1),
                errorName: error.name,
                errorMessage: error.message,
                occurredAt: new Date().toISOString(),
            },
        };
        console.error('[API] Network request failed:', errorInfo);
        reportErrorToServer(errorInfo);

        // Not retried - a replayed POST could create duplicate records.
        throw e;
    }

    if (!response.ok) {
        let errorData: unknown;
        try {
            errorData = await response.json();
        } catch {
            errorData = null;
        }

        if (response.status === 401 && !isSessionExpiryExempt(endpoint)) {
            window.dispatchEvent(new CustomEvent('auth:session-expired'));
        }

        // Handle 402 Payment Required (Insufficient Credits).
        if (response.status === 402 && !skipInsufficientCreditsDialog) {
            window.dispatchEvent(new CustomEvent<InsufficientCreditsDetail | null>('credits:insufficient', {
                detail: toInsufficientCreditsDetail(errorData),
            }));
        }

        // Retry with backoff for transient Lambda cold start errors (502/503/504)
        const isTransient = [502, 503, 504].includes(response.status);
        if (isTransient && _retryCount < 2) {
            const delay = _retryCount === 0 ? 1500 : 3000;
            await new Promise(r => setTimeout(r, delay));
            return apiFetch(endpoint, {
                ...options,
                _retryCount: _retryCount + 1,
                _clientRequestId: clientRequestId,
                _requestStartedAt: requestStartedAt,
            });
        }

        // Log failed requests for debugging (especially 4xx/5xx that aren't 401)
        if (response.status !== 401) {
            const errorInfo = {
                endpoint,
                method,
                status: response.status,
                errorData,
                context: {
                    ...getTransportContext(clientRequestId, requestStartedAt, _retryCount + 1),
                    responseClientRequestId: response.headers.get(CLIENT_REQUEST_ID_HEADER),
                },
            };
            console.error('[API] Request failed:', errorInfo);
            reportErrorToServer(errorInfo);
        }

        throw new ApiError(
            response.status,
            `API request failed: ${response.status}`,
            errorData,
        );
    }

    // A reachable server means queued reports can be delivered now.
    void flushErrorQueue();

    // Handle 204 No Content.
    if (response.status === 204) {
        return undefined as T;
    }

    try {
        return (await response.json()) as T;
    } catch (e) {
        // A 2xx that is not JSON - e.g. CloudFront rewriting an error to /index.html.
        const error = e instanceof Error ? e : new Error(String(e));
        const errorInfo = {
            endpoint,
            method,
            status: response.status,
            errorData: null,
            context: {
                ...getTransportContext(clientRequestId, requestStartedAt, _retryCount + 1),
                responseClientRequestId: response.headers.get(CLIENT_REQUEST_ID_HEADER),
                contentType: response.headers.get('Content-Type'),
                errorName: error.name,
                errorMessage: error.message,
                occurredAt: new Date().toISOString(),
            },
        };
        console.error('[API] Invalid JSON response:', errorInfo);
        reportErrorToServer(errorInfo);

        throw new ApiError(response.status, 'Invalid JSON response');
    }
}

export const api = {
    get: <T>(endpoint: string, options?: RequestOptions) =>
        apiFetch<T>(endpoint, { ...options, method: 'GET' }),

    post: <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
        apiFetch<T>(endpoint, { ...options, method: 'POST', body }),

    put: <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
        apiFetch<T>(endpoint, { ...options, method: 'PUT', body }),

    patch: <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
        apiFetch<T>(endpoint, { ...options, method: 'PATCH', body }),

    delete: <T>(endpoint: string, options?: RequestOptions) =>
        apiFetch<T>(endpoint, { ...options, method: 'DELETE' }),
};

export * from './types';
export * from './batch';
